import {
  KeyValueList,
  MetricTile,
  MetricTileGrid,
  MetricTileProps,
  Skeleton,
} from "@validereinc/common-components";
import {
  DelayActionType,
  WorkflowTaskType,
  WorkflowType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import {
  datetimeFormatter,
  timeDurationToTimeUnits,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { startCase } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./WorkflowActionDelay.module.scss";

const { flattenWorkflowSteps } = Workflows.logic;

const cx = classNames.bind(styles);

export const WorkflowActionDelay = ({
  workflowConfiguration,
  workflowTask,
  stepId,
  isLoading,
}: {
  workflowTask?: WorkflowTaskType;
  workflowConfiguration?: WorkflowType["config"];
  stepId?: string;
  isLoading?: boolean;
}) => {
  // This state is updated every minute to trigger remaining time calculation only if workflowTask exists:
  const [remainingTimeTrigger, setRemainingTimeTrigger] = useState(
    new Date().getTime()
  );

  useEffect(() => {
    if (!workflowTask) return;
    const timeRefreshInterval = setInterval(() => {
      setRemainingTimeTrigger(new Date().getTime());
    }, 60000);

    return () => {
      if (timeRefreshInterval) clearInterval(timeRefreshInterval);
    };
  }, [workflowTask]);

  const remainingTime: MetricTileProps[] = useMemo(() => {
    if (!workflowTask) return [];

    const delayDate = Date.parse(workflowTask?.description);

    if (!isNaN(delayDate)) {
      const now = Date.now();
      const timeRemaining = delayDate - now;
      if (timeRemaining > 0) {
        return timeDurationToTimeUnits(timeRemaining).map((tile) => ({
          title: "",
          value: tile.value,
          unit: tile.unit,
        })) as MetricTileProps[];
      }
    }

    return [];
  }, [workflowTask, remainingTimeTrigger]);

  const step = flattenWorkflowSteps(workflowConfiguration?.steps)?.[
    stepId ?? workflowTask?.step_id ?? ""
  ]?.task as DelayActionType;

  const totalDelayTime: Array<{ value: string; unit: string }> = useMemo(() => {
    if (!step) return [];

    if (step.time_period !== "days") {
      let unit = startCase(step.time_period);
      if (step.duration === 1) {
        unit = unit.substring(0, unit.length - 1);
      }
      return [
        {
          value: `${step.duration}`,
          unit,
        },
      ];
    }

    const weeks = Math.floor(step.duration / 7);
    const days = step.duration - weeks * 7;

    return [
      ...(weeks > 0
        ? [
            {
              value: `${weeks}`,
              unit: weeks === 1 ? "Week" : "Weeks",
            },
          ]
        : []),
      ...(days > 0
        ? [
            {
              value: `${days}`,
              unit: days === 1 ? "Day" : "Days",
            },
          ]
        : []),
    ];
  }, [step]);

  const renderRemainingTime = () => {
    if (remainingTime.length === 0) return null;
    return (
      <>
        <div className={cx("title")}>Remaining Time</div>
        <div className={cx("remaining-time-tiles-container")}>
          {remainingTime.map((remainingTimeTile) => (
            <MetricTile
              key={remainingTimeTile.unit}
              className={cx("remaining-time-tile")}
              {...remainingTimeTile}
            />
          ))}
        </div>
        {workflowTask?.description && (
          <KeyValueList
            variant="shaded"
            data={[
              {
                title: "Expected Completion",
                value: datetimeFormatter(new Date(workflowTask.description)),
              },
              {
                title: "Total Delay from Start",
                value: totalDelayTime
                  .map((i) => `${i.value.toString()} ${i.unit}`)
                  .join(" "),
              },
            ]}
          />
        )}
      </>
    );
  };

  const renderTotalDelayTime = () => {
    if (totalDelayTime.length === 0 || remainingTime.length > 0) return null;
    return (
      <>
        <div className={cx("title")}>Total Delay Time</div>
        <MetricTileGrid
          data={totalDelayTime.map((e) => ({
            title: "",
            value: e.value,
            unit: e.unit,
            className: cx("total-delay-time-tile"),
          }))}
        />
      </>
    );
  };

  return (
    <Skeleton isLoading={isLoading}>
      {renderTotalDelayTime()}
      {renderRemainingTime()}
    </Skeleton>
  );
};
